import { Menu, Dropdown, Space, Tooltip, Divider, Popover, Checkbox, Badge } from 'antd';
import Icon, {
  StarFilled,
  QuestionCircleOutlined,
  SettingOutlined,
  RightOutlined,
  PoweroffOutlined,
} from '@ant-design/icons';
import { css } from '@emotion/css';
import { useState, useEffect, useMemo, useRef } from 'react';
import styled from '@emotion/styled';
import useRoles from 'hooks/business/useRoles';
import StoreSelector from 'components/StoreSelector';
import { useMemoizedFn, useMount } from 'ahooks';
import { useRefreshIsOnlyOneAssistant } from 'hooks/business/isOnlyOneAssistant';
import useIsAdminViewWithMultiAssist from 'hooks/business/useIsAdminViewWithMultiAssist';
import { ReactComponent as SvgWait } from './assets/wait.svg';
import { ReactComponent as SvgFilterUnread } from './assets/filterUnread.svg';
import { ReactComponent as SvgAll } from './assets/all.svg';
import { ReactComponent as SvgFilter } from './assets/filter.svg';
import { ReactComponent as SvgReply } from './assets/reply.svg';
import { ReactComponent as SvgReception } from './assets/reception.svg';
import { ReactComponent as SvgUnchecked } from './assets/unchecked.svg';
import { ReactComponent as SvgDuringSales } from './assets/duringSales.svg';
import { ReactComponent as SvgAfterSales } from './assets/afterSales.svg';
import { ReactComponent as SvgBeforeSales } from './assets/beforeSales.svg';
import { ReactComponent as SvgCS } from './assets/cs.svg';
import { ReactComponent as SvgMediation } from './assets/mediation.svg';
import { ReactComponent as SvgReputation } from './assets/reputation.svg';
import { ReactComponent as SvgTodo } from 'views/Dashboard/assets/todo.svg';
import { ReactComponent as SvgExcludeAffiliate } from './assets/excludeAffiliate.svg';
import { ReactComponent as SvgOnlyAffiliate } from './assets/onlyAffiliate.svg';
import { ReactComponent as SvgFilterQAPair } from './assets/filterQAPair.svg';

import { useOrgConfigStateValue } from 'hooks/business/useOrgConfig';
import { useReceptionConfig } from 'views/Setting/ConversationSetting/useReceptionConfig';
import { FilterAdvance, FilterAdvanceRef, useShowShopeeAffiliate } from './FilterAdvance';
import {
  ConversationFilterAdvanceMode,
  useFilterName,
  useFilterOptions,
  useFilterToBeCheckedEnable,
  useManualCloseEnable,
} from 'hooks/business/conversationsHooks/useFilterAdvance';
import { debounce } from 'lodash-es';
import { useHasMercadoChatPermission } from 'hooks/business/im/useMercadoChatPermission';
import { useOnlyMercadoPlatform } from 'hooks/business/im/useOnlyMercadoPlatform';
import { zIndex } from 'services/config/styles';
import { isMac } from 'utils/is';
import { useTranslate } from '@tolgee/react';
import { ImportantTodoModal, useOnlyMyValue } from './FilterTodo';
import {
  getHasImportantTodoUnprocessedConv,
  getImportantTodoConvCount,
} from 'services/api/conversation.convList';
import { FilterAssistant } from './FilterAssistant';
import posthog from '@leyan/lytics';
import { useQuery } from '@tanstack/react-query';
import { StoreSelectorValue } from 'components/StoreSelector/interface';
import { eventBus } from 'services/eventBus';
import { useKeyboardHandler } from 'hooks/useKeyboardManager';
import { KEYBOARD_PRIORITY } from 'hooks/useKeyboardManager/KeyboardEventManager';
import { useQAPairOpenedWithKeepAlive } from 'hooks/business/im/useQAPair';
import {
  useCategoryAwareFilterAdvance,
  useCategoryAwareFilterAdvanceMode,
  useSetCategoryAwareFilterAdvance,
  useSetCategoryAwareFilterAdvanceMode,
  useCategoryAwareStoreFilter,
  useSetCategoryAwareStoreFilter,
  useSetCategoryAwareSort,
  useCategoryAwareSort,
  useClearCategoryFilterState,
} from 'hooks/business/conversationsHooks/useCategoryFilterState';
import { queries } from 'services/queries';
import { ConversationCategoryType } from 'hooks/business/conversationsHooks/useConversationCategory';

const Container = styled.div`
  border-bottom: 1px solid var(--fett-border-default);
  padding: 0;
  display: flex;
  height: 40px;
  align-items: center;
  > .ant-badge > sup {
    top: 2px;
    right: 6px;
  }
`;

const cssFilterFont = css`
  color: var(--fett-text-secondary) !important;
  margin: 0 4px !important;

  .anticon {
    width: 14px;
    height: 14px;
    color: var(--fett-text-secondary);
    font-size: 14px;
    margin-right: 4px;
  }
`;

const Filter = styled.div<{ active: boolean }>`
  padding: 0 6px;
  color: ${({ active }) =>
    active ? 'var(--fett-app-primary-color)' : 'var(--fett-text-secondary)'};
  cursor: pointer;
  display: flex;
  align-items: center;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  &:hover {
    color: var(--fett-app-primary-color);
  }
  > .anticon {
    margin-right: 4px;
    font-size: 16px;
    color: currentColor;
  }
`;

const emptyList: any[] = [];

interface FiltersBarProps {
  category: ConversationCategoryType;
}

/** 会话列表过滤: 国家; 星标,未读,全部 */
function FiltersBar({ category }: FiltersBarProps) {
  const [menuShow, setMenuShow] = useState(false);

  const isMineMode = category === 'mine';

  const {
    whiteListConfig: { UNPROCESSED_WITH_BOT_REPLIED = false, SIMPLUS_CONV_STATUS = false } = {},
  } = useOrgConfigStateValue();

  const { showShopeeAffiliate } = useShowShopeeAffiliate();

  const toBeCheckedEnable = useFilterToBeCheckedEnable();

  const { isDisplayTobeCheckedConversation = false } = useReceptionConfig() || {};

  const { isSuperviser } = useRoles();
  // 美客多接待权限
  const hasMercadoChatPermission = useHasMercadoChatPermission();
  // 只有美客多平台
  const onlyMercadoPlatform = useOnlyMercadoPlatform();

  const refreshIsOnlyAssistant = useRefreshIsOnlyOneAssistant();

  const onlyMy = useOnlyMyValue();

  const { startFilter, toBeCheckedFilter } = useFilterOptions();

  const categoryAwareSort = useCategoryAwareSort(category);
  const setConversationSort = useSetCategoryAwareSort();

  // 使用分类感知的 hooks（始终调用，通过参数控制）
  const categoryFilterAdvance = useCategoryAwareFilterAdvance(category);
  const categoryFilterAdvanceMode = useCategoryAwareFilterAdvanceMode(category);
  const categoryStoreFilter = useCategoryAwareStoreFilter(category);

  const setCategoryFilterAdvance = useSetCategoryAwareFilterAdvance();
  const setCategoryFilterAdvanceMode = useSetCategoryAwareFilterAdvanceMode();
  const setCategoryStoreFilter = useSetCategoryAwareStoreFilter();
  const clearCategoryFilterState = useClearCategoryFilterState();

  const setConversationFilterAdvance = useMemoizedFn(
    (
      value: Parameters<typeof setCategoryFilterAdvance>[1],
      mode?: ConversationFilterAdvanceMode,
    ) => {
      console.log('[set] category %s, value %s', category, value);
      setCategoryFilterAdvance(category, value, mode);
      if (mode === 'single') {
        filterAdvanceRef?.current?.clean();
      }
    },
  );

  const isAdminViewWithMultiAssist = useIsAdminViewWithMultiAssist();
  const filterName = useFilterName({
    filterAdvance: categoryFilterAdvance,
    mode: categoryFilterAdvanceMode,
  });

  const qaPairOpenedWithKeepAlive = useQAPairOpenedWithKeepAlive();

  const [hasImportantTodoUnprocessed, setHasImportantTodoUnprocessed] = useState(false);
  const [importantTodoCount, setImportantTodoCount] = useState<{
    allCount: number;
    unprocessedCount: number;
  }>();

  const { data: assistantList } = useQuery({
    ...queries.accounts.assistantFromSameDept,
    enabled: menuShow,
  });

  const importantTodoParams = useMemo(() => {
    return { ...categoryStoreFilter, onlyMy };
  }, [onlyMy, categoryStoreFilter]);

  useQuery({
    queryKey: ['importantTodoUnprocessed', importantTodoParams],
    queryFn: () => getHasImportantTodoUnprocessedConv(importantTodoParams),
    refetchInterval: 60 * 1000,
    onSuccess: (data) => {
      setHasImportantTodoUnprocessed(data.has);
    },
  });

  useQuery({
    queryKey: ['importantTodoCount', importantTodoParams],
    queryFn: () => getImportantTodoConvCount(importantTodoParams),
    onSuccess: (data) => {
      setImportantTodoCount(data);
    },
    enabled: menuShow,
  });

  const filterAdvanceRef = useRef<FilterAdvanceRef | null>(null);

  const changeToSingleMode = useMemoizedFn(() => {
    setCategoryFilterAdvanceMode(category, 'single');
    filterAdvanceRef?.current?.clean();
  });

  const manualCloseEnable = useManualCloseEnable();

  const { t } = useTranslate();

  const [todoModalVisible, setTodoModalVisible] = useState(false);
  const todoModal = (
    <ImportantTodoModal
      visible={todoModalVisible}
      onCancel={() => setTodoModalVisible(false)}
      onUpdate={() => {
        if (categoryFilterAdvance?.statusFilters?.includes('IMPORTANT_TODO')) {
          setConversationFilterAdvance({ statusFilters: ['IMPORTANT_TODO'] }, 'single');
        }
      }}
    />
  );

  const filtersArr = useMemo(() => {
    const common = [
      {
        key: 'inReception',
        name: isAdminViewWithMultiAssist
          ? t('scene.chatWorkspace.conversationList.quickFilter.action.inReception')
          : t('scene.chatWorkspace.conversationList.label.withAssignment', {
              baseLabel: t('scene.chatWorkspace.conversationList.quickFilter.action.inReception'),
              assignedToMe: t('scene.chatWorkspace.conversationList.filter.assignedToMe'),
            }),
        value: {
          statusFilters: ['IN_RECEPTION'],
        },
        tips: t(isAdminViewWithMultiAssist ? 'inreception_tips' : 'inreception_assign_to_me_tips'),
        icon: <Icon component={SvgReception} />,
        extra: (
          <SettingOutlined
            onClick={(e) => {
              e.stopPropagation();
              eventBus.emit('SHOW_SYSTEM_MODAL', {
                activeKey: 'conversation',
              });
              setMenuShow(false);
            }}
          />
        ),
      },
      {
        key: 'replied',
        name: isAdminViewWithMultiAssist
          ? t('scene.chatWorkspace.conversationList.quickFilter.toRespond')
          : t('scene.chatWorkspace.conversationList.label.withAssignment', {
              baseLabel: t('scene.chatWorkspace.conversationList.quickFilter.toRespond'),
              assignedToMe: t('scene.chatWorkspace.conversationList.filter.assignedToMe'),
            }),
        value: UNPROCESSED_WITH_BOT_REPLIED
          ? undefined
          : {
              statusFilters: isDisplayTobeCheckedConversation
                ? ['UNPROCESSED', 'BOT_REPLIED']
                : ['UNPROCESSED'],
            },
        tips: t(isAdminViewWithMultiAssist ? 'unreplied_tips' : 'unreplied_assign_to_me_tips'),
        icon: <Icon component={SvgReply} />,
        popoverContent: UNPROCESSED_WITH_BOT_REPLIED && (
          <div className="py-3 px-4">
            <Checkbox.Group
              value={categoryFilterAdvance?.statusFilters}
              onChange={debounce((value) => {
                setConversationFilterAdvance(
                  {
                    statusFilters: value,
                  },
                  'single',
                );
              }, 500)}
            >
              <Space direction="vertical">
                {toBeCheckedFilter.map((value) => (
                  <Checkbox key={value.value} value={value.value}>
                    {value.name}
                  </Checkbox>
                ))}
              </Space>
            </Checkbox.Group>
          </div>
        ),
      },
      ...(toBeCheckedEnable
        ? [
            {
              key: 'botReplied',
              name: t('scene.chatWorkspace.conversationList.quickFilter.action.pendingCheck'),
              value: {
                statusFilters: ['BOT_REPLIED'],
              },
              tips: t('scene.chatWorkspace.conversationList.quickFilter.hint.toBeChecked'),
              icon: <Icon component={SvgUnchecked} />,
            },
          ]
        : emptyList),
      ...(isMineMode
        ? [
            {
              key: 'unread',
              name: t('scene.chatWorkspace.conversationList.quickFilter.unread'),
              value: {
                statusFilters: ['UNREAD'],
              },
              icon: <Icon component={SvgFilterUnread} />,
            },
          ]
        : emptyList),
      ...(manualCloseEnable
        ? [
            {
              key: 'manualClose',
              name: t('scene.chatWorkspace.conversationList.advancedFilter.label.manuallyClosed'),
              value: {
                statusFilters: ['MANUAL_CLOSE'],
              },
              icon: <PoweroffOutlined />,
            },
          ]
        : emptyList),
      ...(isAdminViewWithMultiAssist
        ? [
            {
              key: 'waitAssign',
              name: t('scene.chatWorkspace.conversationList.quickFilter.action.pendingAssignment'),
              value: {
                statusFilters: ['WAIT_ASSIGN'],
              },
              tips: isAdminViewWithMultiAssist
                ? t('scene.chatWorkspace.conversationList.quickFilter.tooltip.toBeAssignedForAdmin')
                : t('scene.chatWorkspace.conversationList.quickFilter.tooltip.toBeAssigned'),
              icon: <Icon component={SvgWait} />,
            },
          ]
        : emptyList),
    ];
    const simplus = [
      {
        key: 'unprocessedAndNotClose',
        name: t('scene.chatWorkspace.conversationList.quickFilter.action.inReception'),
        value: {
          statusFilters: ['UNPROCESSED', 'NOT_CLOSE'],
        },
        icon: <Icon component={SvgReception} />,
      },
      {
        key: 'botReplied',
        name: isAdminViewWithMultiAssist
          ? t('scene.chatWorkspace.conversationList.quickFilter.action.pendingCheck')
          : t('scene.chatWorkspace.conversationList.label.withAssignment', {
              baseLabel: t('scene.chatWorkspace.conversationList.quickFilter.action.pendingCheck'),
              assignedToMe: t('scene.chatWorkspace.conversationList.filter.assignedToMe'),
            }),
        value: {
          statusFilters: ['BOT_REPLIED'],
        },
        icon: <Icon component={SvgReply} />,
      },
      ...(isAdminViewWithMultiAssist
        ? [
            {
              key: 'manualClose',
              name: t('scene.chatWorkspace.conversationList.advancedFilter.label.manuallyClosed'),
              value: {
                statusFilters: ['MANUAL_CLOSE'],
              },
              icon: <PoweroffOutlined />,
            },
          ]
        : emptyList),
      ...(isAdminViewWithMultiAssist
        ? [
            {
              key: 'waitAssign',
              name: isAdminViewWithMultiAssist
                ? t('scene.chatWorkspace.conversationList.quickFilter.action.pendingAssignment')
                : t('scene.chatWorkspace.conversationList.label.withAssignment', {
                    baseLabel: t(
                      'scene.chatWorkspace.conversationList.quickFilter.action.pendingAssignment',
                    ),
                    assignedToMe: t('scene.chatWorkspace.conversationList.filter.assignedToMe'),
                  }),
              value: {
                statusFilters: ['WAIT_ASSIGN'],
              },
              tips: isAdminViewWithMultiAssist
                ? t('scene.chatWorkspace.conversationList.quickFilter.tooltip.toBeAssignedForAdmin')
                : t('scene.chatWorkspace.conversationList.quickFilter.tooltip.toBeAssigned'),
              icon: <Icon component={SvgWait} />,
            },
          ]
        : emptyList),
    ];
    return [
      ...(isMineMode
        ? [
            {
              key: 'importantTodo',
              name: `${t(
                'scene.chatWorkspace.conversationList.quickFilter.action.importantTasks',
              )}(${importantTodoCount?.allCount || 0})`,
              value: { statusFilters: ['IMPORTANT_TODO'] },
              icon: <Icon component={SvgTodo} />,
              hotkey: `${isMac ? 'ctrl' : 'alt'}+d`,
              badge: importantTodoCount?.unprocessedCount ? (
                <Badge count={importantTodoCount.unprocessedCount} />
              ) : null,
              extra: (
                <SettingOutlined
                  onClick={(e) => {
                    e.stopPropagation();
                    setTodoModalVisible(true);
                    setMenuShow(false);
                  }}
                />
              ),
            },
          ]
        : emptyList),
      ...(SIMPLUS_CONV_STATUS ? simplus : common),
      ...(qaPairOpenedWithKeepAlive
        ? [
            {
              key: 'qaPair',
              name: t('scene.chatWorkspace.conversationList.quickFilter.action.answerSubmission'),
              value: {
                existKnowledgePopup: true,
              },
              icon: <Icon component={SvgFilterQAPair} />,
            },
          ]
        : emptyList),
      ...(hasMercadoChatPermission
        ? [
            {
              key: 'reputation',
              name: t('scene.chatWorkspace.conversationList.tag.reputationImpact'),
              value: {
                statusFilters: ['REPUTATION'],
              },
              icon: <Icon component={SvgReputation} />,
            },
            {
              key: 'mediation',
              name: t('scene.chatWorkspace.conversationList.tag.inMediation'),
              value: {
                statusFilters: ['MEDIATION'],
              },
              icon: <Icon component={SvgMediation} />,
            },
          ]
        : emptyList),
      ...(onlyMercadoPlatform
        ? emptyList
        : [
            {
              key: 'beforeSales',
              name: t('scene.chatWorkspace.conversationList.quickFilter.action.presales'),
              value: {
                salesStatusFilters: ['BEFORE_SALES_STATUS'],
              },
              icon: <Icon component={SvgBeforeSales} />,
            },
            {
              key: 'inSales',
              name: t('scene.chatWorkspace.conversationList.quickFilter.action.inSales'),
              value: {
                salesStatusFilters: ['DURING_SALES_STATUS'],
              },
              icon: <Icon component={SvgDuringSales} />,
            },
            {
              key: 'afterSales',
              name: t('scene.chatWorkspace.conversationList.quickFilter.action.aftersales'),
              value: {
                salesStatusFilters: ['AFTER_SALES_STATUS'],
              },
              icon: <Icon component={SvgAfterSales} />,
            },
          ]),
      {
        key: 'starred',
        name: t('scene.chatWorkspace.conversationList.quickFilter.action.starred'),
        icon: <StarFilled />,
        popoverContent: (
          <>
            <div className="px-[16px] pb-[6px] pt-[12px]">
              <Checkbox
                checked={
                  Number(categoryFilterAdvance?.asteriskFilters?.length) ===
                  Number(startFilter?.length)
                }
                indeterminate={
                  Number(categoryFilterAdvance?.asteriskFilters?.length) > 0 &&
                  Number(categoryFilterAdvance?.asteriskFilters?.length) <
                    Number(startFilter?.length)
                }
                onChange={(e) => {
                  setConversationFilterAdvance(
                    {
                      asteriskFilters: e.target.checked ? startFilter.map((v) => v.value) : [],
                    },
                    'single',
                  );
                }}
              >
                {t('common.action.selectAll')}
              </Checkbox>
            </div>
            <Divider style={{ margin: '0 0' }} />
            <Checkbox.Group
              value={categoryFilterAdvance?.asteriskFilters}
              onChange={debounce((value) => {
                setConversationFilterAdvance(
                  {
                    asteriskFilters: value,
                  },
                  'single',
                );
              }, 500)}
            >
              <Space direction="vertical" className="px-[16px] pb-[12px] pt-[6px]">
                {startFilter.map((value) => (
                  <Checkbox key={value.value} value={value.value}>
                    {value.icon}
                    {value.name}
                  </Checkbox>
                ))}
              </Space>
            </Checkbox.Group>
          </>
        ),
      },
      ...(isAdminViewWithMultiAssist
        ? [
            {
              key: 'customerService',
              name: t('scene.chatWorkspace.conversationList.quickFilter.action.csAgent'),
              icon: <Icon component={SvgCS} />,
              popoverContent: assistantList ? (
                <FilterAssistant
                  category={category}
                  assistantList={assistantList}
                  onChange={changeToSingleMode}
                />
              ) : null,
            },
          ]
        : emptyList),
      ...(showShopeeAffiliate
        ? [
            {
              key: 'excludeAffiliate',
              name: t('scene.chatWorkspace.conversationList.quickFilter.action.shopeeBuyerChat'),
              value: {
                excludeShopeeAffiliate: true,
              },
              icon: <Icon component={SvgExcludeAffiliate} />,
            },
            {
              key: 'onlyAffiliate',
              name: t(
                'scene.chatWorkspace.conversationList.quickFilter.action.shopeeAffiliateChat',
              ),
              value: {
                onlyShopeeAffiliate: true,
              },
              icon: <Icon component={SvgOnlyAffiliate} />,
            },
          ]
        : []),
    ];
  }, [
    t,
    isAdminViewWithMultiAssist,
    UNPROCESSED_WITH_BOT_REPLIED,
    isDisplayTobeCheckedConversation,
    categoryFilterAdvance?.statusFilters,
    categoryFilterAdvance?.asteriskFilters,
    toBeCheckedFilter,
    toBeCheckedEnable,
    manualCloseEnable,
    isMineMode,
    importantTodoCount,
    SIMPLUS_CONV_STATUS,
    qaPairOpenedWithKeepAlive,
    hasMercadoChatPermission,
    onlyMercadoPlatform,
    startFilter,
    assistantList,
    category,
    changeToSingleMode,
    showShopeeAffiliate,
    setConversationFilterAdvance,
  ]);

  useMount(() => {
    if (isSuperviser) {
      refreshIsOnlyAssistant();
    }
  });

  // 不再从 localStorage 回填筛选状态：以 IndexDB->Jotai 的 hydrate 为准

  const filterAll = () => {
    setConversationFilterAdvance(undefined, 'single');
    setMenuShow(false);
    filterAdvanceRef?.current?.clean();
  };

  const onStoreSelectorChange = (value: StoreSelectorValue) => {
    setCategoryStoreFilter(category, value);
  };

  const clearFilterAndRefresh = () => {
    clearCategoryFilterState(category);
    setMenuShow(false);
    filterAdvanceRef?.current?.clean();
  };

  useKeyboardHandler(
    isMac ? ['ctrl+a'] : ['alt+a'],
    () => {
      filterAll();
      posthog.capture('$hotKey', {
        hotKey: `${isMac ? 'Ctrl' : 'Alt'}+a`,
      });
    },
    {
      eventType: 'keydown',
      priority: KEYBOARD_PRIORITY.GLOBAL,
      preventDefault: true,
      stopPropagation: true,
    },
  );

  useKeyboardHandler(
    isMac ? ['ctrl+d'] : ['alt+d'],
    () => {
      setConversationFilterAdvance({ statusFilters: ['IMPORTANT_TODO'] }, 'single');
      setMenuShow(false);
      posthog.capture('$hotKey', {
        hotKey: `${isMac ? 'Ctrl' : 'Alt'}+d`,
      });
    },
    {
      eventType: 'keydown',
      priority: KEYBOARD_PRIORITY.GLOBAL,
      preventDefault: true,
      stopPropagation: true,
    },
  );

  const menuFilter = (
    <Menu>
      <Menu.Item key="FilterAll" className={cssFilterFont} onClick={filterAll}>
        <Space size={4} align="center">
          <Icon component={SvgAll} />
          {t('scene.chatWorkspace.conversationList.quickFilter.action.allChats')}
          <span style={{ fontSize: 12, color: '#B5B9BF' }}>{isMac ? 'ctrl' : 'alt'}+a</span>
        </Space>
      </Menu.Item>
      <Menu.Divider />
      {filtersArr.map((v) => {
        const content = (
          <div className="flex items-center justify-between">
            <Space size={4} align="center">
              <div>
                {v.icon} {v.name}
              </div>
              {v.tips ? (
                // eslint-disable-next-line react/no-danger
                <Tooltip title={<div dangerouslySetInnerHTML={{ __html: v.tips }} />}>
                  <QuestionCircleOutlined
                    className={css`
                      &.anticon {
                        color: #84868c;
                      }
                    `}
                  />
                </Tooltip>
              ) : null}
              {v.hotkey && <span style={{ fontSize: 12, color: '#B5B9BF' }}>{v.hotkey}</span>}
              {v.badge}
            </Space>
            {v.extra && (
              <div>
                <Divider type="vertical" />
                {v.extra}
              </div>
            )}
          </div>
        );

        return (
          <Menu.Item
            className={cssFilterFont}
            key={v.key}
            disabled={v.disabled}
            onClick={
              v.value
                ? () => {
                    if (
                      v.value.statusFilters?.includes('IN_RECEPTION') &&
                      ['waitAsc', 'waitDesc'].includes(categoryAwareSort)
                    ) {
                      setConversationSort(category, 'asc');
                    }
                    setConversationFilterAdvance(v.value, 'single');
                    setMenuShow(false);
                  }
                : undefined
            }
          >
            {v.popoverContent ? (
              <Popover overlayClassName={popoverCss} placement="right" content={v.popoverContent}>
                <div className="flex items-center justify-between">
                  {content}
                  <RightOutlined />
                </div>
              </Popover>
            ) : (
              content
            )}
          </Menu.Item>
        );
      })}
      <Menu.Divider />
      <Menu.Item key="FilterAdvance">
        <FilterAdvance
          ref={filterAdvanceRef}
          assistantList={assistantList}
          filterAdvance={categoryFilterAdvance}
          onConfirm={(values) => {
            if (
              values?.statusFilters?.includes('IN_RECEPTION') &&
              ['waitAsc', 'waitDesc'].includes(categoryAwareSort)
            ) {
              setConversationSort(category, 'asc');
            }
            setMenuShow(false);

            const hasValues = !!(
              values?.statusFilters?.length ||
              values?.asteriskFilters?.length ||
              values?.salesStatusFilters?.length ||
              values?.assistantFilters?.length ||
              values?.excludeShopeeAffiliate ||
              values?.onlyShopeeAffiliate ||
              values?.existKnowledgePopup
            );
            setConversationFilterAdvance(values, hasValues ? 'multiple' : 'single');
          }}
        />
      </Menu.Item>
    </Menu>
  );

  useEffect(() => {
    const handleClearFilter = () => {
      clearFilterAndRefresh();
    };
    eventBus.on('CONV_LIST_FILTER_CLEAR', handleClearFilter);
    return () => {
      eventBus.off('CONV_LIST_FILTER_CLEAR', handleClearFilter);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Container>
      <StoreSelector
        scene="BASIC_RECEPTION"
        value={categoryStoreFilter}
        onChange={onStoreSelectorChange}
      />
      <Dropdown
        overlay={menuFilter}
        visible={menuShow}
        onVisibleChange={setMenuShow}
        trigger={['click']}
        placement="bottomRight"
        overlayStyle={{
          zIndex: zIndex.higher,
        }}
      >
        <Badge dot={hasImportantTodoUnprocessed}>
          <Filter active={!!filterName}>
            <Icon
              component={SvgFilter}
              className={css`
                svg {
                  width: 18px;
                  height: 18px;
                }
              `}
            />
            {filterName}
          </Filter>
        </Badge>
      </Dropdown>
      {todoModal}
    </Container>
  );
}

export default FiltersBar;

const popoverCss = css`
  .ant-popover-inner-content {
    max-height: 360px;
    overflow: auto;
    padding: 0;
  }
`;
